import React from 'react';

const ResponsiveContainer = ({ 
  children, 
  className = '', 
  maxWidth = 'max-w-none',
  padding = 'responsive-padding',
  margin = 'responsive-margin'
}) => {
  return (
    <div className={`w-full mx-auto ${maxWidth} ${padding} ${margin} ${className}`}>
      {children}
    </div>
  );
};

export default ResponsiveContainer;

// Responsive text components
export const ResponsiveText = ({ 
  children, 
  className = '', 
  size = 'responsive-text',
  as: Component = 'p'
}) => {
  return (
    <Component className={`${size} ${className}`}>
      {children}
    </Component>
  );
};

export const ResponsiveHeading = ({ 
  children, 
  className = '', 
  level = 1,
  size = 'responsive-heading'
}) => {
  const Component = `h${level}`;
  return (
    <Component className={`${size} font-bold leading-tight ${className}`}>
      {children}
    </Component>
  );
};

// Responsive grid components
export const ResponsiveGrid = ({ 
  children, 
  className = '', 
  cols = 'responsive-grid-cols',
  gap = 'responsive-gap'
}) => {
  return (
    <div className={`responsive-grid ${cols} ${gap} ${className}`}>
      {children}
    </div>
  );
};

// Responsive card component
export const ResponsiveCard = ({ 
  children, 
  className = '', 
  padding = 'responsive-padding',
  hover = false
}) => {
  return (
    <div className={`card ${padding} ${hover ? 'card-hover cursor-default' : ''} ${className}`}>
      {children}
    </div>
  );
};

// Responsive button component
export const ResponsiveButton = ({ 
  children, 
  className = '', 
  variant = 'primary',
  size = 'responsive',
  ...props 
}) => {
  const baseClasses = 'font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const variantClasses = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500 dark:ring-offset-secondary-800',
    secondary: 'bg-secondary-100 hover:bg-secondary-200 text-secondary-700 focus:ring-secondary-500 dark:bg-secondary-700 dark:hover:bg-secondary-600 dark:text-secondary-200 dark:ring-offset-secondary-800',
    danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 dark:bg-red-700 dark:hover:bg-red-800 dark:ring-offset-secondary-800'
  };
  
  const sizeClasses = {
    responsive: 'min-h-[44px] py-2 px-4 text-sm sm:py-2.5 sm:px-5 sm:text-base',
    small: 'min-h-[36px] py-1.5 px-3 text-xs',
    medium: 'min-h-[44px] py-2 px-4 text-sm',
    large: 'min-h-[48px] py-3 px-6 text-base'
  };
  
  return (
    <button 
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Responsive input component
export const ResponsiveInput = ({ 
  className = '', 
  size = 'responsive',
  ...props 
}) => {
  const sizeClasses = {
    responsive: 'min-h-[44px] py-2 px-3 text-sm sm:text-base',
    small: 'min-h-[36px] py-1.5 px-2 text-xs',
    medium: 'min-h-[44px] py-2 px-3 text-sm',
    large: 'min-h-[48px] py-3 px-4 text-base'
  };
  
  return (
    <input 
      className={`input-field ${sizeClasses[size]} ${className}`}
      {...props}
    />
  );
};

// Responsive spacing utilities
export const ResponsiveSpacing = ({ 
  children, 
  className = '', 
  padding = 'responsive-padding',
  margin = 'responsive-margin',
  gap = 'responsive-gap'
}) => {
  return (
    <div className={`${padding} ${margin} ${gap} ${className}`}>
      {children}
    </div>
  );
};
